/* ==========================================================================
   Lucretia · Project case-study page
   Shared behaviour for the project-case-* sections.

   Four jobs, all optional and independent, so a page that omits a section
   still works:
     1. Measure the theme chrome once and publish it as CSS variables.
     2. Scroll-spy the pinned section nav.
     3. Run the gallery lightbox (click, Esc, arrows, backdrop, focus trap).
     4. Unpin the About spec panel when it is taller than the screen.

   Loaded with defer by every section, so it can be requested many times on
   one page. The guard below makes every call after the first a no-op.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__pcsInit) return;
  window.__pcsInit = true;

  var root = document.documentElement;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function on(el, type, fn, opts) {
    if (el) el.addEventListener(type, fn, opts || false);
  }

  /* ----------------------------------------------------------------------
     1. Theme chrome
     The sticky header and any announcement bar above the hero are measured
     once and written to :root, so every section reads the same number and
     the hero cannot change height mid-scroll.
     ---------------------------------------------------------------------- */

  /* Candidates are looked up once and re-read on every scroll, because a
     theme can switch its header from relative to fixed as the page moves
     (overlay headers on a full-bleed hero do exactly that). Measuring only
     at load is what let the section nav sit on top of the site header. */
  var CHROME_SELECTOR =
    '#HeaderWrapper, #SiteHeader, #shopify-section-header, .site-header, .header-wrapper, ' +
    'header[role="banner"], [id^="shopify-section"][id*="header"], ' +
    '#shopify-section-announcement, .announcement-bar, .site-header__announcement';

  /* Every match, used for "what is covering the top right now": one header
     matches several of these selectors at once, and only the inner one may
     be the element that actually goes fixed, so nothing can be dropped. */
  var chromeEls = [];
  /* Outermost matches only, used for the one-off hero offset, which is a SUM
     and would otherwise count the same bar three times. */
  var chromeOuter = [];

  function collectChrome() {
    chromeEls = Array.prototype.slice.call(document.querySelectorAll(CHROME_SELECTOR)).filter(function (el) {
      /* Never measure our own nav, or anything inside it. */
      return !el.closest('.pcs-nav-wrap') && !el.querySelector('.pcs-nav');
    });
    chromeOuter = chromeEls.filter(function (el) {
      return !chromeEls.some(function (other) {
        return other !== el && other.contains(el);
      });
    });
  }

  function isPinned(el) {
    if (!el) return false;
    var pos = getComputedStyle(el).position;
    return pos === 'fixed' || pos === 'sticky';
  }

  /* How much chrome is covering the top of the window RIGHT NOW. */
  function syncHeaderOffset() {
    var h = 0;
    chromeEls.forEach(function (el) {
      if (!isPinned(el)) return;
      var rect = el.getBoundingClientRect();
      if (!rect.height || rect.width < window.innerWidth * 0.5) return;
      /* Sitting across the top edge, not scrolled out or pushed down. */
      if (rect.top > 1 || rect.bottom <= 0) return;
      if (rect.bottom > h) h = rect.bottom;
    });
    if (h > window.innerHeight * 0.4) h = 0;
    root.style.setProperty('--pcs-header-h', Math.round(h) + 'px');
  }

  /* Chrome that sits above the hero eats into the first screen whether it is
     pinned or not. Measured once, so the hero cannot change height mid-scroll. */
  function measureHeroOffset() {
    var above = 0;
    chromeOuter.forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (!rect.height) return;
      if (rect.top < window.innerHeight) above += rect.height;
    });
    if (above > window.innerHeight * 0.5) above = 0;
    root.style.setProperty('--pcs-hero-offset', Math.round(above) + 'px');
  }

  function measureNav() {
    var navWrap = document.querySelector('.pcs-nav-wrap');
    if (!navWrap) return;
    var navH = navWrap.getBoundingClientRect().height;
    if (navH) root.style.setProperty('--pcs-nav-h', Math.round(navH) + 'px');
  }

  function measureChrome() {
    collectChrome();
    measureHeroOffset();
    syncHeaderOffset();
    measureNav();
  }

  /* ----------------------------------------------------------------------
     2. Section nav scroll-spy
     ---------------------------------------------------------------------- */

  function initNav() {
    var nav = document.querySelector('.pcs-nav');
    if (!nav) return;

    var links = Array.prototype.slice.call(nav.querySelectorAll('.pcs-nav__item'));
    if (!links.length) return;

    var targets = links.map(function (a) {
      var href = a.getAttribute('href') || '';
      if (href.charAt(0) !== '#' || href.length < 2) return null;
      try {
        return document.querySelector(href);
      } catch (e) {
        return null;
      }
    });

    function mark(index) {
      links.forEach(function (a, i) {
        if (i === index) a.classList.add('is-active');
        else a.classList.remove('is-active');
      });
    }

    if (!('IntersectionObserver' in window)) {
      mark(0);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var i = targets.indexOf(entry.target);
          if (i > -1) mark(i);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );

    targets.forEach(function (t) {
      if (t) observer.observe(t);
    });

    mark(0);
  }

  /* ----------------------------------------------------------------------
     3. Gallery lightbox
     One lightbox element per gallery section; tiles are <button>s so they
     are keyboard reachable without any extra work here.
     ---------------------------------------------------------------------- */

  function initLightbox(box) {
    var gallery = box.previousElementSibling;
    while (gallery && !gallery.querySelector('[data-pcs-gallery]')) {
      gallery = gallery.previousElementSibling;
    }
    if (!gallery) return;

    var tiles = Array.prototype.slice.call(gallery.querySelectorAll('[data-pcs-tile]'));
    if (!tiles.length) return;

    var img = box.querySelector('[data-pcs-lb-img]');
    var count = box.querySelector('[data-pcs-lb-count]');
    var btnPrev = box.querySelector('[data-pcs-lb-prev]');
    var btnNext = box.querySelector('[data-pcs-lb-next]');
    var btnClose = box.querySelector('[data-pcs-lb-close]');
    var current = 0;
    var lastFocused = null;

    function show(i) {
      current = (i + tiles.length) % tiles.length;
      var tile = tiles[current];
      if (img) {
        img.setAttribute('src', tile.getAttribute('data-full') || '');
        img.setAttribute('alt', tile.getAttribute('data-alt') || '');
      }
      if (count) count.textContent = current + 1 + ' / ' + tiles.length;
    }

    function open(i) {
      lastFocused = document.activeElement;
      show(i);
      box.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      if (btnClose) btnClose.focus();
    }

    function close() {
      box.hidden = true;
      document.documentElement.style.overflow = '';
      if (img) img.setAttribute('src', '');
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    }

    tiles.forEach(function (tile, i) {
      on(tile, 'click', function () {
        open(i);
      });
    });

    on(btnPrev, 'click', function () {
      show(current - 1);
    });
    on(btnNext, 'click', function () {
      show(current + 1);
    });
    on(btnClose, 'click', close);

    /* Clicking the backdrop, but not the picture or the controls. */
    on(box, 'click', function (e) {
      if (e.target === box) close();
    });

    on(document, 'keydown', function (e) {
      if (box.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        show(current - 1);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        show(current + 1);
        return;
      }
      if (e.key !== 'Tab') return;

      /* Keep focus inside the lightbox while it is open. */
      var focusable = Array.prototype.slice
        .call(box.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'))
        .filter(function (el) {
          return el.offsetParent !== null;
        });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  function initLightboxes() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pcs-lightbox]'), initLightbox);
  }

  /* ----------------------------------------------------------------------
     4. About spec panel
     Pinning a panel taller than the viewport hides its CTA behind a nested
     scrollbar, so in that case it is left in the flow.
     ---------------------------------------------------------------------- */

  function syncAside() {
    Array.prototype.forEach.call(document.querySelectorAll('.pcs-about__aside'), function (aside) {
      aside.classList.remove('is-unpinned');
      var available =
        window.innerHeight -
        (parseFloat(getComputedStyle(root).getPropertyValue('--pcs-header-h')) || 0) -
        (parseFloat(getComputedStyle(root).getPropertyValue('--pcs-nav-h')) || 0) -
        20;
      if (aside.scrollHeight > available) aside.classList.add('is-unpinned');
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */

  function boot() {
    measureChrome();
    initNav();
    initLightboxes();
    syncAside();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The header can pin and unpin as the page moves, so the offset is read on
     every frame the window scrolls, not once at load. */
  var ticking = false;
  on(
    window,
    'scroll',
    function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        syncHeaderOffset();
        ticking = false;
      });
    },
    { passive: true }
  );

  /* Fonts and images landing late change the measurements. */
  on(window, 'load', function () {
    measureChrome();
    syncAside();
  });

  var resizeTimer = null;
  on(window, 'resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      measureChrome();
      syncAside();
    }, reduceMotion ? 0 : 150);
  });

  /* The theme editor rebuilds a section without reloading the page. */
  document.addEventListener('shopify:section:load', function () {
    measureChrome();
    initNav();
    initLightboxes();
    syncAside();
  });
})();
